/**
* Apostille (อนุสัญญากรุงเฮก) — 500+ keyword universe for the
* /apostille/* Q&A cluster. Split into core/definition, document types,
* Hague member destinations, inbound apostille, pricing & urgency,
* and long-tail advisory phrasings.
*/

export type ApostilleKeywordGroup = { id: string; label: string; keywords: string[] };

const CORE = [
  "apostille ประเทศไทย", "ขอ apostille ที่ไหน", "apostille กรมการกงสุล",
  "apostille แจ้งวัฒนะ", "อนุสัญญากรุงเฮก apostille", "hague apostille convention thailand",
  "apostille thailand service", "apostille คืออะไร", "apostille ต่างจากนิติกรณ์",
  "apostille vs legalization", "ตราอาโพสทีล", "apostille certificate thailand",
  "apostille เอกสารไทย", "apostille เอกสารราชการไทย", "รับทำ apostille ครบวงจร",
  "บริการ apostille ด่วน", "apostille ใช้เวลากี่วัน", "apostille ราคาเท่าไหร่",
  "apostille ค่าธรรมเนียม", "apostille ต้องแปลก่อนไหม", "apostille ต้องมี notary ไหม",
  "apostille ออนไลน์", "e-apostille thailand", "ตรวจสอบ apostille ปลอม",
  "apostille verification thailand", "apostille หมดอายุไหม", "apostille กี่ประเทศใช้ได้",
  "apostille 120 ประเทศ", "apostille สำหรับคนไทยในต่างประเทศ",
  "apostille agent bangkok", "apostille service near me", "apostille ตัวแทนยื่นแทน",
  "apostille มอบอำนาจ", "apostille ต่างจังหวัดส่งไปรษณีย์", "apostille รับส่งเอกสารถึงบ้าน",
  "apostille ปรึกษาฟรี", "apostille เอกสารด่วน 1 วัน", "apostille ราคาถูกเชื่อถือได้",
  "ขั้นตอน apostille ทีละขั้น", "apostille ต้องใช้ต้นฉบับไหม",
];

const DOCS = [
  "apostille สูติบัตร", "apostille ทะเบียนสมรส", "apostille ใบรับรองโสด",
  "apostille ใบหย่า", "apostille ทะเบียนบ้าน", "apostille บัตรประชาชน",
  "apostille ใบเปลี่ยนชื่อ", "apostille ใบมรณบัตร", "apostille ใบประวัติอาชญากรรม",
  "apostille หนังสือรับรองความประพฤติ", "apostille ปริญญาบัตร", "apostille ทรานสคริปต์",
  "apostille ใบรับรองการศึกษา", "apostille หนังสือรับรองบริษัท", "apostille ภ.พ.20",
  "apostille งบการเงิน", "apostille หนังสือมอบอำนาจ", "apostille affidavit",
  "apostille สัญญาซื้อขาย", "apostille คำพิพากษาศาล", "apostille ใบรับรองแพทย์",
  "apostille ใบขับขี่", "apostille พินัยกรรม", "apostille ใบทะเบียนพาณิชย์",
  "apostille certificate of origin", "apostille invoice", "apostille ใบอนุญาตประกอบวิชาชีพ",
  "apostille ใบสำคัญถิ่นที่อยู่", "apostille หนังสือเดินทาง", "apostille ใบสุทธิ ปพ.1",
  "apostille birth certificate thailand", "apostille marriage certificate thailand",
  "apostille degree thailand", "apostille transcript thailand",
  "apostille power of attorney thailand", "apostille police clearance thailand",
  "apostille company affidavit thailand", "apostille court judgment thailand",
  "apostille medical certificate thailand", "apostille single status thailand",
];

const COUNTRY_BASE = [
  ["สหรัฐอเมริกา", "usa"], ["สหราชอาณาจักร", "united kingdom"], ["ออสเตรเลีย", "australia"],
  ["นิวซีแลนด์", "new zealand"], ["ญี่ปุ่น", "japan"], ["เกาหลีใต้", "south korea"],
  ["เยอรมนี", "germany"], ["ฝรั่งเศส", "france"], ["อิตาลี", "italy"], ["สเปน", "spain"],
  ["โปรตุเกส", "portugal"], ["เนเธอร์แลนด์", "netherlands"], ["เบลเยียม", "belgium"],
  ["สวิตเซอร์แลนด์", "switzerland"], ["ออสเตรีย", "austria"], ["สวีเดน", "sweden"],
  ["นอร์เวย์", "norway"], ["เดนมาร์ก", "denmark"], ["ฟินแลนด์", "finland"],
  ["โปแลนด์", "poland"], ["สาธารณรัฐเช็ก", "czech republic"], ["ฮังการี", "hungary"],
  ["กรีซ", "greece"], ["ไอร์แลนด์", "ireland"], ["จีน", "china"], ["อินเดีย", "india"],
  ["สิงคโปร์", "singapore"], ["ฟิลิปปินส์", "philippines"], ["รัสเซีย", "russia"],
  ["บราซิล", "brazil"], ["เม็กซิโก", "mexico"], ["แอฟริกาใต้", "south africa"],
  ["ตุรกี", "turkey"], ["อิสราเอล", "israel"], ["ชิลี", "chile"], ["อาร์เจนตินา", "argentina"],
];
const COUNTRIES = COUNTRY_BASE.flatMap(([th, en]) => [
  `apostille ไป${th}`,
  `apostille for ${en}`,
  `apostille เอกสารยื่น${th}`,
]);

const INTENT = [
  "apostille ด่วนที่สุด", "apostille รอรับได้", "apostille วันเดียวเสร็จ",
  "apostille สำหรับวีซ่า", "apostille สำหรับแต่งงานต่างประเทศ",
  "apostille สำหรับเรียนต่อ", "apostille สำหรับสมัครงานต่างประเทศ",
  "apostille สำหรับขอสัญชาติ", "apostille สำหรับเปิดบริษัทต่างประเทศ",
  "apostille สำหรับ skill assessment", "apostille สำหรับซื้ออสังหาต่างประเทศ",
  "apostille สำหรับรับมรดกต่างประเทศ", "apostille สำหรับฟ้องคดีต่างประเทศ",
  "apostille สำหรับเปิดบัญชีธนาคารต่างประเทศ", "apostille สำหรับย้ายถิ่นฐาน",
  "apostille เชียงใหม่", "apostille ภูเก็ต", "apostille พัทยา", "apostille ขอนแก่น",
  "apostille หาดใหญ่", "apostille อุดรธานี", "apostille นครราชสีมา", "apostille เชียงราย",
  "apostille สุราษฎร์ธานี", "apostille ระยอง", "apostille หนองคาย", "apostille อุบลราชธานี",
  "apostille นนทบุรี ปทุมธานี", "apostille สมุทรปราการ", "apostille ทั่วประเทศส่ง EMS",
];

const INBOUND = [
  "เอกสาร apostille ต่างประเทศใช้ในไทย", "inbound apostille thailand",
  "apostille ต่างประเทศต้องแปลไทยไหม", "apostille ต่างประเทศต้องผ่านกงสุลไทยไหม",
  "รับรองเอกสาร apostille อเมริกาใช้ในไทย", "apostille uk ใช้ในไทย",
  "apostille เยอรมัน ใช้ในไทย", "apostille ฝรั่งเศส ใช้ในไทย",
  "apostille ญี่ปุ่น ใช้ในไทย", "apostille ออสเตรเลีย ใช้ในไทย",
  "apostille ใบเกิดต่างประเทศแจ้งเกิดในไทย", "apostille ใบรับรองโสดจดทะเบียนสมรสไทย",
  "apostille ใบหย่าต่างประเทศบันทึกในไทย", "apostille หนังสือรับรองบริษัทต่างชาติยื่น DBD",
  "apostille คำพิพากษาต่างประเทศใช้ในศาลไทย", "apostille บำนาญขอวีซ่าเกษียณไทย",
  "apostille ใบตรวจประวัติต่างประเทศขอ work permit",
  "แปลเอกสาร apostille เป็นไทย", "รับรองคำแปล apostille กรมการกงสุล",
  "apostille ต่างประเทศตีกลับเพราะแปลผิด",
];

const LONGTAIL = [
  "apostille ต้องทำ notary ก่อนหรือไม่", "apostille เอกสารเอกชนทำได้ไหม",
  "apostille สำเนาถูกต้องใช้ได้ไหม", "apostille ต้องคัดต้นฉบับใหม่ไหม",
  "apostille แปลชื่อไม่ตรงพาสปอร์ตทำไง", "apostille ถูกปฏิเสธเพราะอะไร",
  "apostille ใช้แทนสถานทูตได้ทุกประเทศไหม", "ประเทศที่ไม่รับ apostille มีอะไร",
  "apostille กับ certified true copy ต่างกันไหม", "apostille กับ sworn translation",
  "apostille กับ notarized translation ต่างกัน", "apostille ต้องยื่นกี่ชุด",
  "apostille ขอสำเนาเพิ่มได้ไหม", "apostille ทำซ้ำได้ไหมถ้าหาย",
  "apostille ตรวจสอบเลขที่ออนไลน์", "apostille กับ e-apostille ต่างกัน",
  "apostille เอกสารสองภาษาได้ไหม", "apostille เอกสารหลายหน้าเย็บเล่มยังไง",
  "apostille สำหรับเด็กและผู้เยาว์", "apostille สำหรับผู้สูงอายุมอบอำนาจ",
];

export const APOSTILLE_KEYWORDS_500: ApostilleKeywordGroup[] = [
  { id: "core", label: "พื้นฐาน / บริการ / ค่าธรรมเนียม", keywords: CORE },
  { id: "documents", label: "ประเภทเอกสาร", keywords: DOCS },
  { id: "country", label: "ประเทศภาคีอนุสัญญากรุงเฮก", keywords: COUNTRIES },
  { id: "intent", label: "วัตถุประสงค์ / ความเร่งด่วน / พื้นที่", keywords: INTENT },
  { id: "inbound", label: "Apostille ต่างประเทศใช้ในไทย", keywords: INBOUND },
  { id: "longtail", label: "คำถามเชิงลึก / ปัญหา", keywords: LONGTAIL },
];

export const APOSTILLE_KEYWORDS_FLAT: string[] = APOSTILLE_KEYWORDS_500.flatMap((g) => g.keywords);
export const APOSTILLE_KEYWORDS_TOTAL = APOSTILLE_KEYWORDS_FLAT.length;

/**
 * Hague member destinations used in the Q&A matrix.
 *
 * ACCURACY NOTE (ตรวจทาน 2026-08-03): ไทยยังไม่ออกตรา Apostille จนกว่าอนุสัญญา
 * จะมีผลกับไทยวันที่ 28 ก.พ. 2027 (hcch.net · mfa.go.th) — note ของแต่ละประเทศ
 * จึงระบุเฉพาะข้อกำหนดเรื่องภาษาคำแปลและหน่วยงานผู้รับเอกสารเท่านั้น
 */
export const APOSTILLE_COUNTRIES = [
  { slug: "usa", th: "สหรัฐอเมริกา", en: "United States", note: "หน่วยงานระดับรัฐปลายทางมักขอคำแปลอังกฤษพร้อมคำรับรองผู้แปล" },
  { slug: "uk", th: "สหราชอาณาจักร", en: "United Kingdom", note: "Home Office/UKVI ขอคำแปลอังกฤษพร้อมคำรับรองผู้แปล" },
  { slug: "australia", th: "ออสเตรเลีย", en: "Australia", note: "หน่วยงานรัฐและการประเมินคุณวุฒิมักขอคำแปลอังกฤษโดยผู้แปล NAATI" },
  { slug: "new-zealand", th: "นิวซีแลนด์", en: "New Zealand", note: "INZ ขอคำแปลอังกฤษพร้อมคำรับรองผู้แปล" },
  { slug: "japan", th: "ญี่ปุ่น", en: "Japan", note: "ศาลากลางหรือสำนักงานเขตปลายทางมักขอคำแปลภาษาญี่ปุ่นเพิ่ม" },
  { slug: "korea", th: "เกาหลีใต้", en: "South Korea", note: "สำนักงานเขตปลายทางมักขอคำแปลภาษาเกาหลีเพิ่ม" },
  { slug: "germany", th: "เยอรมนี", en: "Germany", note: "Standesamt มักขอคำแปลจากผู้แปลสาบานตนภาษาเยอรมัน" },
  { slug: "france", th: "ฝรั่งเศส", en: "France", note: "มักขอคำแปลจาก traducteur assermenté" },
  { slug: "italy", th: "อิตาลี", en: "Italy", note: "Comune มักขอคำแปลภาษาอิตาลีที่ได้รับการรับรอง" },
  { slug: "spain", th: "สเปน", en: "Spain", note: "มักขอคำแปลจาก traductor jurado" },
  { slug: "netherlands", th: "เนเธอร์แลนด์", en: "Netherlands", note: "Gemeente มักขอคำแปลอังกฤษหรือดัตช์" },
  { slug: "switzerland", th: "สวิตเซอร์แลนด์", en: "Switzerland", note: "ข้อกำหนดต่างกันตาม Kanton ปลายทาง" },
  { slug: "sweden", th: "สวีเดน", en: "Sweden", note: "Skatteverket มักขอคำแปลอังกฤษ" },
  { slug: "norway", th: "นอร์เวย์", en: "Norway", note: "UDI มักขอคำแปลอังกฤษ" },
  { slug: "denmark", th: "เดนมาร์ก", en: "Denmark", note: "Familieretshuset มักขอคำแปลอังกฤษหรือเดนิช" },
  { slug: "finland", th: "ฟินแลนด์", en: "Finland", note: "DVV มักขอคำแปลอังกฤษ" },
  { slug: "poland", th: "โปแลนด์", en: "Poland", note: "USC มักขอคำแปลภาษาโปแลนด์ที่ได้รับการรับรอง" },
  { slug: "czech", th: "สาธารณรัฐเช็ก", en: "Czech Republic", note: "มักขอคำแปลภาษาเช็กที่ได้รับการรับรอง" },
  { slug: "greece", th: "กรีซ", en: "Greece", note: "มักขอคำแปลภาษากรีกที่ได้รับการรับรอง" },
  { slug: "ireland", th: "ไอร์แลนด์", en: "Ireland", note: "GRO มักขอคำแปลอังกฤษพร้อมคำรับรองผู้แปล" },
  { slug: "portugal", th: "โปรตุเกส", en: "Portugal", note: "Conservatória มักขอคำแปลภาษาโปรตุเกส" },
  { slug: "china", th: "จีน", en: "China", note: "จีนเป็นภาคีอนุสัญญาตั้งแต่ปี 2023 และหน่วยงานปลายทางมักขอคำแปลภาษาจีน" },
  { slug: "india", th: "อินเดีย", en: "India", note: "หน่วยงานปลายทางมักขอคำแปลอังกฤษพร้อมคำรับรองผู้แปล" },
  { slug: "singapore", th: "สิงคโปร์", en: "Singapore", note: "หน่วยงานปลายทางมักขอคำแปลอังกฤษ" },
  { slug: "philippines", th: "ฟิลิปปินส์", en: "Philippines", note: "PSA/DFA มักขอคำแปลอังกฤษ" },
  { slug: "russia", th: "รัสเซีย", en: "Russia", note: "มักขอคำแปลภาษารัสเซียที่ได้รับการรับรอง" },
  { slug: "brazil", th: "บราซิล", en: "Brazil", note: "มักขอคำแปลภาษาโปรตุเกสที่ได้รับการรับรอง" },
  { slug: "mexico", th: "เม็กซิโก", en: "Mexico", note: "มักขอคำแปลภาษาสเปนที่ได้รับการรับรอง" },
  { slug: "south-africa", th: "แอฟริกาใต้", en: "South Africa", note: "DHA มักขอคำแปลอังกฤษ" },
  { slug: "turkey", th: "ตุรกี", en: "Türkiye", note: "มักขอคำแปลภาษาตุรกีที่ได้รับการรับรอง" },
] as const;


/** Document types used in the Q&A matrix. */
export const APOSTILLE_DOC_TYPES = [
  { slug: "birth-certificate", th: "สูติบัตร", en: "birth certificate" },
  { slug: "marriage-certificate", th: "ทะเบียนสมรส", en: "marriage certificate" },
  { slug: "single-status", th: "ใบรับรองโสด", en: "certificate of single status" },
  { slug: "divorce", th: "ใบหย่า", en: "divorce certificate" },
  { slug: "house-registration", th: "ทะเบียนบ้าน", en: "house registration" },
  { slug: "name-change", th: "ใบเปลี่ยนชื่อ-สกุล", en: "name change certificate" },
  { slug: "police-clearance", th: "ใบตรวจประวัติอาชญากรรม", en: "police clearance certificate" },
  { slug: "degree", th: "ปริญญาบัตร", en: "degree certificate" },
  { slug: "transcript", th: "ทรานสคริปต์", en: "academic transcript" },
  { slug: "company-affidavit", th: "หนังสือรับรองบริษัท", en: "company affidavit" },
  { slug: "power-of-attorney", th: "หนังสือมอบอำนาจ", en: "power of attorney" },
  { slug: "court-judgment", th: "คำพิพากษาศาล", en: "court judgment" },
  { slug: "medical-certificate", th: "ใบรับรองแพทย์", en: "medical certificate" },
  { slug: "death-certificate", th: "ใบมรณบัตร", en: "death certificate" },
  { slug: "financial-statement", th: "งบการเงิน", en: "financial statement" },
] as const;
